import { useCallback, useEffect, useRef, useState } from "react";
import {
  EMPTY_ARRAY,
  EMPTY_STRING,
  INDEX,
  MODAL_SIZE,
} from "../../../constants";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, isEqual } from "lodash";
import { checkIsConnectionComplete } from "../services.helpers";
import { selectSelectedNode } from "../";
import Modal, { setSelectedModal } from "../../../components/organisms/Modal";
import { MODALS } from "../../../components/organisms/AppModals/modals.constants";
import { redTrashIcon } from "../../../assets/icons";
import ManageExpression from "./ManageExpression";
import { Table } from "../../../components/molecules";
import SelectFieldModal from "../../../components/organisms/AppModals/ServiceModals/SelectFieldModal";
import { FIELDS } from "../datamodels.constants";

const { MD } = MODAL_SIZE;
const { FIELD_VALUE_UPDATE_MODAL } = MODALS;
const { FIRST } = INDEX;
const SET_FIELD_VALUE_NODE_ID = 30;
const CREATE_VARIABLE_RECORD_NODE_ID = 28;
const { MODEL_NAME } = FIELDS;
const CUSTOM_MODEL = "customModel";

const FieldValueUpdates = ({
  name,
  values,
  handleChange,
  collections,
  nodes,
  edges,
}) => {
  const dispatch = useDispatch();
  const selectedNode = useSelector(selectSelectedNode);
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState(EMPTY_ARRAY);
  const [selectedModelName, setSelectedModelName] = useState();
  const filteredProperties = useRef(EMPTY_ARRAY);
  const areCustomVariableRequired = useRef(
    selectedNode.nodeId === SET_FIELD_VALUE_NODE_ID
  );
  const shouldShowParentModelName = useRef(false);
  const preSelectedValues = useRef(values[name]);

  useEffect(() => {
    if (values[name] && !isEqual(preSelectedValues.current, values[name])) {
      preSelectedValues.current = values[name];
      handleChange(values[name], name);
    }
  }, [values, name, handleChange]);

  useEffect(() => {
    areCustomVariableRequired.current =
      selectedNode.nodeId === SET_FIELD_VALUE_NODE_ID;
  }, [selectedNode]);

  const handleModelNameChanges = useCallback(() => {
    if (isEmpty(values[name])) return;
    const shouldRemoveValues = values[name][FIRST].id;
    shouldRemoveValues && handleChange(EMPTY_ARRAY, name);
    if (selectedModelName) {
      const collectionName = collections.find(
        (collection) => collection.refName === selectedModelName
      );
      const collectionProperties = collectionName?.schema.properties;
      const properties = collectionProperties?.filter(
        (property) => !property.meta.isKey
      );
      if (!properties) return;
      const shouldUpdateCollectionProperties = !values[name].every(
        ({ fieldPath }) =>
          properties.find(({ schema }) => schema?.key === fieldPath)
      );
      if (shouldUpdateCollectionProperties) handleChange(EMPTY_ARRAY, name);
    }
  }, [collections, selectedModelName, handleChange, name, values]);

  const handleCustomValueChanges = useCallback(() => {
    if (!shouldShowParentModelName.current) {
      const shouldUpdateCustomValues =
        values[name] &&
        !values[name].every((value) =>
          filteredProperties.current.find(
            (property) =>
              value.id === property.id &&
              value.name === property.name &&
              value.type === property.type
          )
        );
      if (shouldUpdateCustomValues) {
        const updatedCustomVariableProperties = values[name].reduce(
          (udpatedProperties, value) => {
            filteredProperties.current.forEach((property) => {
              if (value.id === property.id)
                udpatedProperties.push({
                  ...property,
                  expression: value.expression,
                });
            });
            return udpatedProperties;
          },
          []
        );
        handleChange(updatedCustomVariableProperties, name);
      }
    } else handleModelNameChanges();
  }, [values, name, handleChange, handleModelNameChanges]);

  const getCustomVariableValues = useCallback(() => {
    const isConnectionComplete = checkIsConnectionComplete(
      selectedNode,
      nodes,
      edges
    );
    if (!isConnectionComplete) return;
    const currentNode = nodes.find(({ id }) => selectedNode.id === id);
    const nonCustomInputHandle = currentNode.data.inputs.find(
      ({ custom }) => !custom
    );
    if (!nonCustomInputHandle) return;
    const nonCustomInputHandleKey = nonCustomInputHandle.key;
    const currentParentNodes = edges.reduce((parentNodes, edge) => {
      if (edge.target !== currentNode.id) return parentNodes;
      if (edge.targetHandle === `tava_${nonCustomInputHandleKey}`) {
        const parentNode = nodes.find(({ id }) => edge.source === id);
        parentNodes.push(parentNode);
        return parentNodes;
      }
      return parentNodes;
    }, []);
    const validParentNode =
      currentParentNodes[FIRST].data.nodeId === CREATE_VARIABLE_RECORD_NODE_ID
        ? currentParentNodes[FIRST]
        : false;

    const parentNodeModelConfig =
      !validParentNode &&
      currentParentNodes[FIRST].data.config.find(
        ({ key }) => key === MODEL_NAME
      );
    const shouldShowCustomVariablesRecordValues =
      validParentNode &&
      checkIsConnectionComplete(validParentNode.data, nodes, edges);
    const shouldShowModelProperties =
      parentNodeModelConfig &&
      checkIsConnectionComplete(currentParentNodes[FIRST].data, nodes, edges);

    if (!(shouldShowCustomVariablesRecordValues || shouldShowModelProperties))
      return;
    if (!validParentNode) {
      const collectionName = parentNodeModelConfig?.defaultValue;
      setSelectedModelName(collectionName);
      shouldShowParentModelName.current = true;
      return;
    }
    shouldShowParentModelName.current = false;
    const properties = validParentNode.data.config.find(
      ({ key }) => key === CUSTOM_MODEL
    ).defaultValue;

    return properties;
  }, [edges, nodes, selectedNode]);

  useEffect(() => {
    if (areCustomVariableRequired.current) {
      const properties = getCustomVariableValues();
      filteredProperties.current = properties ?? EMPTY_ARRAY;
      handleCustomValueChanges();
    }
  }, [getCustomVariableValues, handleCustomValueChanges]);

  useEffect(() => {
    const hasValues = !isEmpty(values[name]);
    if (hasValues) {
      const preSelectedProperties =
        !shouldShowParentModelName.current &&
        values[name].reduce((existingProperties, value) => {
          const properties =
            !isEmpty(filteredProperties.current) &&
            filteredProperties.current.find(
              (property) => property.id === value.id
            );
          if (properties) existingProperties.push(properties);
          return existingProperties;
        }, []);
      setSelectedProperties(preSelectedProperties);
    }
  }, [values, name, shouldShowModal]);

  const handleShowModal = useCallback(() => {
    dispatch(setSelectedModal(FIELD_VALUE_UPDATE_MODAL));
    setShouldShowModal(true);
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(setSelectedModal(null));
    setShouldShowModal(false);
  }, [dispatch]);

  const handleAllPropertySelection = (e, rows) => {
    const selectedRows = e.target.checked ? rows : EMPTY_ARRAY;
    setSelectedProperties(selectedRows);
  };

  const handleCustomPropertySelection = useCallback(() => {
    const updatedPropertyObject = isEmpty(values[name])
      ? selectedProperties.map((selectedProperty) => ({
          ...selectedProperty,
          expression: EMPTY_STRING,
        }))
      : selectedProperties.reduce((updatedProperties, selectedProperty) => {
          const property = values[name].find(
            ({ id }) => id === selectedProperty.id
          );
          updatedProperties.push(property || selectedProperty);
          return updatedProperties;
        }, []);
    handleChange(updatedPropertyObject, name);
    handleClose();
  }, [selectedProperties, handleClose, values, name, handleChange]);

  const handleSelect = useCallback(() => {
    handleCustomPropertySelection();
  }, [handleCustomPropertySelection]);

  const handleSinglePropertySelection = (e, selectedProperty) => {
    if (e.target.checked)
      setSelectedProperties([...selectedProperties, selectedProperty]);
    else
      setSelectedProperties(
        selectedProperties.filter(
          (property) => property.name !== selectedProperty.name
        )
      );
  };

  const handlePropertyDelete = (id) => {
    const updatedProperties = values[name].filter((value) => value.id !== id);
    handleChange(updatedProperties, name);
    setSelectedProperties(updatedProperties);
  };

  const propertyFormatter = (row) => (
    <div>
      <input
        type="checkbox"
        value={row}
        checked={selectedProperties.some((each) => each.name === row.name)}
        className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
        onChange={(e) => {
          handleSinglePropertySelection(e, row);
        }}
      />
    </div>
  );

  const headerFormatter = (rows) => {
    const isCheckedAll =
      filteredProperties.current.length === selectedProperties.length;
    return (
      <div>
        <input
          type="checkbox"
          value={rows}
          defaultChecked={isCheckedAll}
          className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
          onClick={(e) => handleAllPropertySelection(e, rows)}
        />
      </div>
    );
  };

  const getRowDescription = (row) => row.type;

  const footer = (
    <div className="p-6 border-t border-gray-700 text-right">
      <button
        className="btn btn-primary-light mr-1"
        type="button"
        onClick={handleClose}
      >
        Cancel
      </button>
      <button
        className="relative btn btn-primary"
        type="submit"
        onClick={handleSelect}
      >
        Select
      </button>
    </div>
  );

  const columns = [
    {
      dataField: "name",
      formatter: propertyFormatter,
      headerFormatter: headerFormatter,
      text: "Name",
    },
    {
      text: "Type",
      className: "text-center",
      width: 350,
      formatter: getRowDescription,
    },
  ];

  return (
    <>
      {shouldShowParentModelName.current && selectedModelName ? (
        <SelectFieldModal
          name={name}
          fieldKey={name}
          values={values}
          collections={collections}
          btnText="Manage Updates"
          title="Select fields to update"
          description="Select fields to update"
          modelName={selectedModelName}
          nodes={nodes}
          edges={edges}
          handleChange={handleChange}
        />
      ) : (
        <div>
          {isEmpty(filteredProperties.current) ? (
            <div className="text-xs p-2 my-1 rounded-md border-2 border-red-500 text-red-600 text-center">
              No Properties Found
            </div>
          ) : (
            <button
              type="button"
              className="rounded items-center p-1 w-full btn-primary-light"
              onClick={handleShowModal}
            >
              Manage Updates
            </button>
          )}
          {shouldShowModal && (
            <Modal
              title="Select fields to update"
              handleClose={handleClose}
              size={MD}
              footer={footer}
            >
              <div className="p-6">
                <label className="text-gray-300 mb-2 flex">
                  Select fields to update
                </label>
                <div>
                  <Table columns={columns} data={filteredProperties.current} />
                </div>
              </div>
            </Modal>
          )}

          {values[name]?.map((value, index) => {
            const label = value.name;
            const id = value.id;
            return (
              <div className="my-2" key={id}>
                <div className="flex w-full justify-between pr-2">
                  <label className="text-sm font-medium w-full truncate ...">
                    {label}
                  </label>
                  <button
                    type="button"
                    className="hover:opacity-80"
                    onClick={() => handlePropertyDelete(id)}
                  >
                    <img src={redTrashIcon} alt="delete" className=" w-5" />
                  </button>
                </div>
                <ManageExpression
                  values={values}
                  name={label}
                  expressionKey={`${name}[${index}].expression`}
                  collections={collections}
                  nodes={nodes}
                  edges={edges}
                  handleChange={(e) =>
                    handleChange(e, `${name}[${index}].expression`)
                  }
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default FieldValueUpdates;
